import { useEffect, useState } from "react";
import { encaixar, primeiroEspacoLivre, type Caixa, type Planta, type TipoDeElemento } from "@bookdental/schemas";

export type Selecao = { tipo: "sala" | "elemento"; id: string } | null;

/** Os mesmos limites do plantaSchema. */
export const TAMANHO = { colunas: { min: 12, max: 48 }, linhas: { min: 8, max: 32 } } as const;
export type LimitesDoTamanho = {
  colunas: { min: number; max: number; travadoPorPeca: boolean };
  linhas: { min: number; max: number; travadoPorPeca: boolean };
};

const caixaDe = (c: Caixa): Caixa => ({ x: c.x, y: c.y, w: c.w, h: c.h });
const novoId = () => `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Estado do editor: as peças, a selecionada e se há alteração por salvar. */
export function useEditorDaPlanta(inicial: Planta | null | undefined) {
  const [planta, setPlanta] = useState<Planta | null>(inicial ?? null);
  const [selecao, setSelecao] = useState<Selecao>(null);
  // O que está salvo: "alterações não salvas" é a diferença entre isso e a planta na tela.
  const [base, setBase] = useState<string | null>(inicial ? JSON.stringify(inicial) : null);
  useEffect(() => {
    if (inicial && !planta) {
      setPlanta(inicial);
      setBase(JSON.stringify(inicial));
    }
  }, [inicial, planta]);
  const sujo = !!planta && JSON.stringify(planta) !== base;

  const pecas = (p: Planta) => [
    ...p.salas.map((s) => ({ tipo: "sala" as const, id: s.salaId, caixa: caixaDe(s) })),
    ...p.elementos.map((e) => ({ tipo: "elemento" as const, id: e.id, caixa: caixaDe(e) })),
  ];
  const outras = (p: Planta, sel: NonNullable<Selecao>) => pecas(p).filter((x) => !(x.tipo === sel.tipo && x.id === sel.id)).map((x) => x.caixa);
  const caixaAtual = (p: Planta, sel: NonNullable<Selecao>) => pecas(p).find((x) => x.tipo === sel.tipo && x.id === sel.id)?.caixa ?? null;

  const mudar = (fn: (p: Planta) => Planta) => setPlanta((p) => (p ? fn(p) : p));

  /** Põe a peça no primeiro espaço livre. Devolve falso se a planta estiver cheia. */
  const adicionar = (sel: NonNullable<Selecao>, tipoDeElemento?: TipoDeElemento) => {
    if (!planta) return false;
    const lugar = primeiroEspacoLivre(planta, pecas(planta).map((x) => x.caixa), 4, 3) ?? primeiroEspacoLivre(planta, pecas(planta).map((x) => x.caixa), 1, 1);
    if (!lugar) return false;
    mudar((p) =>
      sel.tipo === "sala"
        ? { ...p, salas: [...p.salas, { salaId: sel.id, ...lugar }] }
        : { ...p, elementos: [...p.elementos, { id: sel.id, tipo: tipoDeElemento ?? "texto", ...lugar }] },
    );
    setSelecao(sel);
    return true;
  };

  /** Nova posição/tamanho, encaixada na grade; esbarrou em outra peça, fica onde estava. */
  const posicionar = (sel: NonNullable<Selecao>, proposta: Caixa) =>
    mudar((p) => {
      const atual = caixaAtual(p, sel);
      if (!atual) return p;
      const c = encaixar(p, outras(p, sel), atual, proposta);
      if (c.x === atual.x && c.y === atual.y && c.w === atual.w && c.h === atual.h) return p;
      return sel.tipo === "sala"
        ? { ...p, salas: p.salas.map((s) => (s.salaId === sel.id ? { ...s, ...c } : s)) }
        : { ...p, elementos: p.elementos.map((e) => (e.id === sel.id ? { ...e, ...c } : e)) };
    });

  const remover = (sel: NonNullable<Selecao>) => {
    mudar((p) => (sel.tipo === "sala" ? { ...p, salas: p.salas.filter((s) => s.salaId !== sel.id) } : { ...p, elementos: p.elementos.filter((e) => e.id !== sel.id) }));
    setSelecao(null);
  };

  const renomear = (id: string, rotulo: string) => mudar((p) => ({ ...p, elementos: p.elementos.map((e) => (e.id === id ? { ...e, rotulo: rotulo || undefined } : e)) }));

  // A planta cresce e encolhe pela direita e por baixo: as peças ficam onde estão, e nenhuma pode
  // ficar de fora.
  const limites = (p: Planta): LimitesDoTamanho => {
    const caixas = pecas(p).map((x) => x.caixa);
    const borda = (lado: (c: Caixa) => number) => Math.max(0, ...caixas.map(lado));
    const direita = borda((c) => c.x + c.w);
    const baixo = borda((c) => c.y + c.h);
    return {
      colunas: { min: Math.max(TAMANHO.colunas.min, direita), max: TAMANHO.colunas.max, travadoPorPeca: direita >= p.colunas && direita > TAMANHO.colunas.min },
      linhas: { min: Math.max(TAMANHO.linhas.min, baixo), max: TAMANHO.linhas.max, travadoPorPeca: baixo >= p.linhas && baixo > TAMANHO.linhas.min },
    };
  };
  const redimensionar = (colunas: number, linhas: number) =>
    mudar((p) => {
      const l = limites(p);
      const c = Math.min(l.colunas.max, Math.max(l.colunas.min, colunas));
      const r = Math.min(l.linhas.max, Math.max(l.linhas.min, linhas));
      return c === p.colunas && r === p.linhas ? p : { ...p, colunas: c, linhas: r };
    });

  return {
    planta,
    selecao,
    setSelecao,
    sujo,
    salvo: () => setBase(JSON.stringify(planta)),
    caixa: (sel: NonNullable<Selecao>) => (planta ? caixaAtual(planta, sel) : null),
    adicionarSala: (salaId: string) => adicionar({ tipo: "sala", id: salaId }),
    adicionarElemento: (tipo: TipoDeElemento) => adicionar({ tipo: "elemento", id: novoId() }, tipo),
    posicionar,
    remover,
    renomear,
    limitesDoTamanho: planta ? limites(planta) : null,
    redimensionar,
  };
}
